import api from '@api';
import Chip from '@components/common/Chip';
import AudienceOverlap from '@components/layout/main/AudienceOverlap';
import AudienceOverview from '@components/layout/main/AudienceOverview';
import InsightChartsBlock from '@components/layout/main/insightCharts/InsightChartsBlock';
import config from '@config';
import type { AudienceType } from '@enums';
import DataQuestionIcon from '@icons/data-question.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { Tooltip } from '@ui/tooltip';
import {
  flattenAgentLinkChildren,
  getAudienceMetadata,
  isInlineChart,
  log,
  mapUrlToInsightChartsBlockProps,
  parseAudienceOverlapIds,
  parseAudienceOverviewTags,
  parseInsightStats
} from '@utils';

import type { MouseEvent, ReactNode } from 'react';
import { Fragment, useCallback } from 'react';
import type { Insight } from 'types/schema';
import Styled from './AgentReference.styled';
import InsightStats from './InsightStats';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: OK
function AgentReference({
  children,
  href,
  ...props
}: {
  children: ReactNode;
  href?: string;
  [key: string]: unknown;
}) {
  const chatId = useChatStore((store) => store.chat.id);
  const setInsightList = useChatStore((store) => store.setInsightList);
  const setRightPanelProps = useShellStore((store) => store.setRightPanelProps);
  const setIsRightPanelOpen = useShellStore(
    (store) => store.setIsRightPanelOpen
  );
  const setIsSidebarExpanded = useShellStore(
    (store) => store.setIsSidebarExpanded
  );

  const handleInsightClick = useCallback(
    (
      event: MouseEvent<HTMLElement>,
      insightIds: string,
      content?: string | null
    ) => {
      event.preventDefault();
      const target = event.currentTarget;

      if (target.classList.contains('active')) {
        setIsRightPanelOpen(false);
        return;
      }

      document.querySelector('.agent-link.active')?.classList.remove('active');
      document
        .querySelector('.insight-stat-item.active')
        ?.classList.remove('active');

      target.classList.add('active');

      log(`Opening sources: ${insightIds}`);

      if (window.innerWidth < config.breakpoints.xxLarge) {
        setIsSidebarExpanded(false);
      }

      setRightPanelProps({
        content: content ?? undefined,
        isOpen: true,
        onToggleCallback: ((element: HTMLElement) => {
          element.classList.toggle('active');
        }).bind(null, target)
      });

      if (insightIds) {
        const insights: Insight[] = insightIds.split(',').map((id) => ({ id }));

        setInsightList(insights);

        api.analytics.openedSources({
          ['chat_id']: chatId,
          ['nb_sources']: insights.length
        });
      }
    },
    [
      chatId,
      setInsightList,
      setIsRightPanelOpen,
      setIsSidebarExpanded,
      setRightPanelProps
    ]
  );

  let url: URL;

  try {
    url = new URL(href);
  } catch {
    // Render links with malformed URLs as plain text
    return <span {...props}>{children}</span>;
  }

  if (url.protocol !== 'spark:') {
    return (
      <a href={href} {...props}>
        {children}
      </a>
    );
  }

  if (url.hostname === 'audience-overview') {
    const audienceId = url.searchParams.get('audienceId');

    if (!audienceId) {
      return null;
    }

    log(`Rendering audience overview for audience id: ${audienceId}`);

    return (
      <Styled.BlockContent>
        <AudienceOverview
          audienceId={audienceId}
          tags={parseAudienceOverviewTags(url.searchParams.get('tags'))}
        />
      </Styled.BlockContent>
    );
  }

  if (url.hostname === 'insight-stats') {
    const stats = parseInsightStats(url.toString());

    if (!stats) {
      return null;
    }

    log(`Rendering insight stats: ${stats.map((s) => s.insightId).join(', ')}`);

    return (
      <Styled.BlockContent>
        <InsightStats insights={stats} onInsightClick={handleInsightClick} />
      </Styled.BlockContent>
    );
  }

  if (url.hostname === 'audience-overlap') {
    const ids = parseAudienceOverlapIds(url.toString());

    if (ids.length === 0) {
      return null;
    }

    log(`Rendering audience overlap: ${ids.join(', ')}`);

    return (
      <Styled.BlockContent>
        <AudienceOverlap audienceIds={ids} />
      </Styled.BlockContent>
    );
  }

  if (url.hostname === 'question-panel') {
    // Panel opens via parseQuestionPanelUrl in Main;
    return <span>{children}</span>;
  }

  const modifiedProps: Record<string, unknown> = {
    ...props,
    href: undefined,
    className: 'agent-link',
    title: undefined
  };
  let content: ReactNode = children;

  if (url.hostname === 'attrs' || url.hostname === 'tooltip') {
    content = (
      <Tooltip
        content={content}
        enterDelay={200}
        enterTouchDelay={200}
        leaveDelay={0}
        variant="white">
        <Styled.Anchor className="agent-link-anchor">
          <DataQuestionIcon className="icon" />
        </Styled.Anchor>
      </Tooltip>
    );
  }

  if (url.hostname === 'audience') {
    log(`Rendering audience chip: ${url.toString()}`);
    const audienceType = url.searchParams.get('type') as AudienceType;
    const { icon, description } = getAudienceMetadata(audienceType);

    modifiedProps.title = description;
    modifiedProps.className = 'audience';
    content = <Chip icon={icon}>{content}</Chip>;
  }

  if (url.hostname === 'sources') {
    const total = url.searchParams.get('total');
    const linkLabel = flattenAgentLinkChildren(content).trim();
    const insightIds = url.searchParams.get('insights');

    if (isInlineChart(url, linkLabel)) {
      return (
        <Styled.BlockContent>
          <InsightChartsBlock
            {...mapUrlToInsightChartsBlockProps(url, linkLabel)}
          />
        </Styled.BlockContent>
      );
    }

    modifiedProps.onClick = (event: MouseEvent<HTMLElement>) => {
      const textElement = event.currentTarget.querySelector('.agent-link-text');

      handleInsightClick(event, insightIds ?? '', textElement?.textContent);
    };

    content = (
      <Fragment data-testid="fragment-content">
        {content && <span className="agent-link-text">{content}</span>}
        <Styled.Anchor data-testid="agent-link-anchor">
          {total && <span>{total}</span>}
          <DataQuestionIcon className="icon" />
        </Styled.Anchor>
      </Fragment>
    );
  }

  return <Styled.Reference {...modifiedProps}>{content}</Styled.Reference>;
}

export default AgentReference;
